/// <amd-module name="Artifacts/Connect/tools/python/Twine" />
import { CopyableCode, DetailsCard, PublishDetailsCard } from "Artifacts/Connect/components/Common";
import * as Res from "Artifacts/Connect/Resources";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { PythonProtocol } from "Artifacts/Connect/tools/python/Protocol";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import * as React from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData } from "VSS/Platform/Page";

const TwineConnect: React.FC<IToolConnectProps<PythonProtocol>> = ({
  protocol,
  feed,
  packagingService,
}) => {
  const url = protocol.getTwineFeedUrl(packagingService, feed);

  return (
    <React.Fragment>
      <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
        <FormatComponent format={Res.ProjectSetup_CredProvider}>
          <b>{Res.ArtifactsKeyring}</b>
          <b>{Res.ArtifactsKeyring}</b>
        </FormatComponent>
        <p>{Res.ProjectSetup_Twine_AddPypirc}</p>
        <CopyableCode ariaLabel={Res.ProjectSetup}>
          <pre>{`[distutils]
Index-servers =
  ${feed.name}

[${feed.name}]
Repository = ${url}`}</pre>
        </CopyableCode>

        <p>
          <FormatComponent format={Res.ProjectSetup_Twine_Recommendation}>
            <code>.pypirc</code>
            <code>[pypi]</code>
            <code>.pypirc</code>
          </FormatComponent>
        </p>
      </DetailsCard>

      <PublishDetailsCard feed={feed}>
        <p>{Res.Twine_CreateDistribution}</p>
        <CopyableCode ariaLabel={Res.PublishPackages}>
          python setup.py sdist bdist_wheel
        </CopyableCode>
        <p>
          {Res.Twine_Publish}
          <br />
          <span className="font-weight-semibold">{Res.Important}: </span>
          <FormatComponent format={Res.Twine_Publish_Note}>
            <code>-r&nbsp;{feed.name}</code>
          </FormatComponent>
        </p>
        <CopyableCode
          ariaLabel={Res.PublishPackages}
        >{`twine upload -r ${feed.name} dist/*`}</CopyableCode>
      </PublishDetailsCard>
    </React.Fragment>
  );
};

class TwineTool implements ITool {
  readonly id = ToolId.twine;
  readonly name = "twine";
  readonly protocol = new PythonProtocol();
  readonly allowNonMembers = false;
  readonly imagePath = `ms.azure-artifacts/connect/img/python.svg`;
  readonly toolLinkId = 2103968;
  readonly Component: React.ComponentType = TwineConnect;

  isEnvironmentSupported(pageData: IPageData): boolean {
    return true;
  }

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return true;
  }

  supportsGet(): boolean {
    return false;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return feed.canPublishPackages;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <TwineConnect
        pageContext={pageContext}
        protocol={this.protocol}
        feed={feed}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }

  public getToolLink = () => ({
    href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
    children: `${Res.CommandLineReference}`,
  });
}

ToolService.registerTool(new TwineTool());
